import React from "react";

const CONTENT = {
  faq: {
    title: "Frequently asked questions",
    body: (
      <div className="space-y-4">
        {[
          ["Why fixed pack sizes?", "All Noyaa Pharma products ship only in the standardized pack sizes 50, 100, 150, 200, 300 and 600 pills. This ensures consistent dosing and transparent flat-tier shipping."],
          ["How is shipping calculated?", "Shipping is based on the combined total of pills across your entire cart: 50–200 pills = $15, 201–300 = $25, 301–600 = $35."],
          ["Is a prescription required?", "Certain products require a valid prescription. They are marked with an Rx badge on the product page. During checkout, our licensed pharmacist reviews your prescription before dispatch."],
          ["What are the min and max order sizes?", "Minimum is 50 pills across the cart, maximum is 600 pills across the cart."],
          ["When am I charged?", "Payment is processed offline after order review. You will not be charged automatically at checkout."],
          ["Can I track my order?", "Yes — sign in and open 'My Orders' to see status updates."],
        ].map(([q,a]) => (
          <details key={q} className="card-flat p-4 group">
            <summary className="font-semibold cursor-pointer flex justify-between">{q}<span className="text-emerald-600">+</span></summary>
            <p className="text-sm text-slate-600 mt-2">{a}</p>
          </details>
        ))}
      </div>
    ),
  },
  shipping: {
    title: "Shipping information",
    body: (
      <div className="space-y-4 text-slate-700 leading-relaxed">
        <p>Shipping charges are automatically calculated based on the total number of pills across your entire cart:</p>
        <div className="card-flat divide-y">
          <div className="p-4 flex justify-between"><span>50 – 200 pills</span><span className="font-mono font-bold">$15</span></div>
          <div className="p-4 flex justify-between"><span>201 – 300 pills</span><span className="font-mono font-bold">$25</span></div>
          <div className="p-4 flex justify-between"><span>301 – 600 pills</span><span className="font-mono font-bold">$35</span></div>
        </div>
        <p>Minimum order is 50 pills; maximum is 600 pills. Orders are packed in tamper-evident, temperature-controlled packaging.</p>
      </div>
    ),
  },
  privacy: {
    title: "Privacy policy",
    body: <div className="space-y-3 text-slate-700 leading-relaxed">
      <p>Noyaa Pharma respects your privacy. We collect the minimum information necessary to fulfill your order (name, contact, shipping address, and — where applicable — prescription documentation).</p>
      <p>Your personal data is stored securely, encrypted in transit and at rest, and is never sold or shared with third parties beyond the parties required to fulfill your order (pharmacist review, courier, payment processor).</p>
      <p>You may request deletion of your account and personal data at any time by contacting our support team.</p>
    </div>,
  },
  terms: {
    title: "Terms & conditions",
    body: <div className="space-y-3 text-slate-700 leading-relaxed">
      <p>By using this website, you agree to purchase medications only for personal use, in accordance with the laws of your jurisdiction. Prescription medications require a valid prescription verified by our licensed pharmacist prior to dispatch.</p>
      <p>Noyaa Pharma does not provide medical advice. Consult a qualified physician before starting, stopping or changing any medication.</p>
      <p>Orders may be cancelled or refunded at Noyaa's discretion in cases where prescription verification fails or a product is unavailable.</p>
    </div>,
  },
  prescription: {
    title: "Prescription & eligibility",
    body: <div className="space-y-3 text-slate-700 leading-relaxed">
      <p>Products marked with the <span className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-blue-200">Rx</span> badge require a valid prescription from a licensed physician.</p>
      <p>During checkout, you will be asked to confirm that you hold a valid prescription. Our licensed pharmacist will contact you at the email you provide to verify your prescription document before dispatch.</p>
      <p>We do not dispense prescription medications without valid verification. We do not advertise prescription medicines as unrestricted and do not make claims of guaranteed cure.</p>
    </div>,
  },
  about: {
    title: "About Noyaa Pharma",
    body: <div className="space-y-3 text-slate-700 leading-relaxed">
      <p>Noyaa Pharma is committed to safe, transparent access to certified pharmaceutical solutions. Every order is reviewed by our licensed pharmacist team, shipped from certified manufacturing partners, and packaged discreetly.</p>
      <p>Our fixed pack-size system and flat-tier shipping keep pricing simple, predictable, and honest.</p>
    </div>,
  },
  payment: {
    title: "Secure & Convenient Payment Options",
    body: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <p>At Noyaa Enterprises, we aim to make the payment process simple and convenient. We support the following payment methods, subject to availability and eligibility:</p>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { name: "PayPal", desc: "Convenient online payments where supported.", color: "from-blue-50 to-sky-50 border-blue-200", label: "text-blue-700" },
            { name: "Western Union", desc: "Transfer services where permitted and available.", color: "from-amber-50 to-yellow-50 border-amber-200", label: "text-amber-700" },
            { name: "Razorpay", desc: "Secure online payment options for eligible transactions.", color: "from-emerald-50 to-teal-50 border-emerald-200", label: "text-emerald-700" },
          ].map((m) => (
            <div key={m.name} className={`rounded-2xl border bg-gradient-to-br ${m.color} p-5`}>
              <div className={`text-xs font-bold uppercase tracking-wider ${m.label}`}>Payment method</div>
              <div className="font-display font-bold text-xl text-slate-900 mt-1">{m.name}</div>
              <p className="text-sm text-slate-600 mt-2">{m.desc}</p>
            </div>
          ))}
        </div>
        <div className="card-flat p-5">
          <div className="font-display font-bold text-lg text-slate-900">Need help with payment?</div>
          <p className="text-sm text-slate-600 mt-2">Contact our team if you have questions about available payment methods, transaction requirements, or payment confirmation. All payments and orders are subject to applicable laws and payment-provider policies.</p>
          <a href="/contact" className="btn-primary mt-4 inline-flex" data-testid="payment-contact-cta">Contact support</a>
        </div>
      </div>
    ),
  },
};

export default function Info({ page }) {
  const c = CONTENT[page];
  return (
    <div className="blz-container py-10 max-w-3xl">
      <h1 className="font-display text-3xl sm:text-4xl font-bold">{c.title}</h1>
      <div className="mt-6">{c.body}</div>
    </div>
  );
}
